import React, { useState, useEffect } from 'react';
import { stockService, supermercadoService, extractErrorDetails } from '../services/api';
import ErrorDisplay from './ErrorDisplay';
import { useAuth } from '../App';

function BulkStockUpdate() {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [productCodeColumn, setProductCodeColumn] = useState('0');
  const [quantityColumn, setQuantityColumn] = useState('1');
  const [locationType, setLocationType] = useState('deposito');
  const [selectedSupermercado, setSelectedSupermercado] = useState('');
  const [supermercados, setSupermercados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSupermercados, setLoadingSupermercados] = useState(false);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  const [result, setResult] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(Date.now());

  const isDeposito = user?.role === 'ROLE_ADMIN_DEPOSITO';
  const isSistema = user?.role === 'ROLE_ADMIN_SISTEMA';

  useEffect(() => {
    // Load supermercados if needed
    if ((isDeposito || isSistema) && locationType === 'supermercado') {
      fetchSupermercados();
    }
  }, [isDeposito, isSistema, locationType]);

  const fetchSupermercados = async () => {
    try {
      setLoadingSupermercados(true);
      const response = await supermercadoService.getAllSupermercados();
      if (response.success) {
        setSupermercados(response.data || []);
      }
    } catch (err) {
      console.error('Error al cargar supermercados:', err);
    } finally {
      setLoadingSupermercados(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      if (!selectedFile.name.toLowerCase().endsWith('.xlsx')) {
        setError('Por favor seleccione un archivo Excel (.xlsx)');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError('');
      setErrorTrace(null);
      setResult(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Por favor seleccione un archivo');
      return;
    }

    if (locationType === 'supermercado' && !selectedSupermercado) {
      setError('Por favor seleccione un supermercado');
      return;
    }

    const productCol = parseInt(productCodeColumn);
    const qtyCol = parseInt(quantityColumn);

    if (isNaN(productCol) || productCol < 0) {
      setError('La columna del código de producto debe ser un número mayor o igual a 0');
      return;
    }

    if (isNaN(qtyCol) || qtyCol < 0) {
      setError('La columna de cantidad debe ser un número mayor o igual a 0');
      return;
    }

    if (productCol === qtyCol) {
      setError('Las columnas del código de producto y cantidad deben ser diferentes');
      return;
    }

    setLoading(true);
    setError('');
    setErrorTrace(null);
    setResult(null);

    try {
      let response;
      if (locationType === 'deposito') {
        response = await stockService.bulkUpdateDepositoStock(file, productCol, qtyCol);
      } else {
        response = await stockService.bulkUpdateSupermercadoStock(
          selectedSupermercado, 
          file, 
          productCol, 
          qtyCol
        );
      }
      
      if (response.success) {
        setResult(response.data);
        setFile(null);
        // Reset file input by changing key
        setFileInputKey(Date.now());
      } else {
        setError(response.message || 'Error al actualizar stock');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al actualizar stock');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bulk-stock-update">
      <h2>Actualización Masiva de Stock</h2>
      
      <div className="import-instructions">
        <h3>Instrucciones</h3>
        <p>Esta funcionalidad permite actualizar el stock de múltiples productos desde un archivo Excel.</p>
        
        <div className="import-notes">
          <h4>Reglas importantes:</h4>
          <ul>
            <li><strong>Cantidad positiva:</strong> se resta del stock actual</li>
            <li><strong>Cantidad negativa:</strong> se suma al stock actual</li>
            <li><strong>Cantidades con decimales:</strong> se redondean automáticamente</li>
            <li>Los productos se buscan por su <strong>código externo</strong></li>
            <li>Si un producto no existe, se reportará en los resultados pero no detendrá el proceso</li>
          </ul>
          
          <h4>Formato del archivo:</h4>
          <ul>
            <li>El archivo debe ser un Excel (.xlsx)</li>
            <li>La primera fila debe contener los encabezados de las columnas</li>
            <li>Debe especificar qué columna contiene el código externo del producto (comienza en 0)</li>
            <li>Debe especificar qué columna contiene la cantidad a ajustar (comienza en 0)</li>
          </ul>
          
          <div className="example-table">
            <h5>Ejemplo de formato:</h5>
            <table className="format-table">
              <thead>
                <tr>
                  <th>Columna 0</th>
                  <th>Columna 1</th>
                  <th>Columna 2</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Código Externo</td>
                  <td>Cantidad</td>
                  <td>Otros datos...</td>
                </tr>
                <tr>
                  <td>PROD001</td>
                  <td>10</td>
                  <td>...</td>
                </tr>
                <tr>
                  <td>PROD002</td>
                  <td>-5</td>
                  <td>...</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="import-form">
        <div className="input-group">
          <label>Ubicación</label>
          <select
            value={locationType}
            onChange={(e) => {
              setLocationType(e.target.value);
              setSelectedSupermercado('');
            }}
            disabled={loading}
          >
            <option value="deposito">Depósito</option>
            {(isDeposito || isSistema) && (
              <option value="supermercado">Supermercado</option>
            )}
          </select>
        </div>

        {locationType === 'supermercado' && (
          <div className="input-group">
            <label>Supermercado</label>
            <select
              value={selectedSupermercado}
              onChange={(e) => setSelectedSupermercado(e.target.value)}
              disabled={loading || loadingSupermercados}
              required
            >
              <option value="">Seleccione un supermercado</option>
              {supermercados.map((super_) => (
                <option key={super_.id} value={super_.id}>
                  {super_.nombre}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="input-group">
          <label htmlFor="fileInput">Archivo Excel (.xlsx)</label>
          <input
            type="file"
            id="fileInput"
            key={fileInputKey}
            accept=".xlsx"
            onChange={handleFileChange}
            disabled={loading}
          />
          {file && (
            <p className="file-selected">
              Archivo seleccionado: <strong>{file.name}</strong>
            </p>
          )}
        </div>

        <div className="column-selectors">
          <div className="input-group">
            <label htmlFor="productCodeColumn">
              Columna del Código Externo del Producto (0-indexed)
            </label>
            <input
              type="number"
              id="productCodeColumn"
              min="0"
              value={productCodeColumn}
              onChange={(e) => setProductCodeColumn(e.target.value)}
              disabled={loading}
              required
            />
            <small>La primera columna es 0, la segunda es 1, etc.</small>
          </div>

          <div className="input-group">
            <label htmlFor="quantityColumn">
              Columna de la Cantidad (0-indexed)
            </label>
            <input
              type="number"
              id="quantityColumn"
              min="0"
              value={quantityColumn}
              onChange={(e) => setQuantityColumn(e.target.value)}
              disabled={loading}
              required
            />
            <small>La primera columna es 0, la segunda es 1, etc.</small>
          </div>
        </div>

        <ErrorDisplay error={error} errorTrace={errorTrace} />

        {result && (
          <div className="import-result">
            <h3>Resultado de la Actualización</h3>
            <div className="result-summary">
              <div className="result-item">
                <span className="result-label">Total de filas procesadas:</span>
                <span className="result-value">{result.totalRows}</span>
              </div>
              <div className="result-item success">
                <span className="result-label">Productos actualizados:</span>
                <span className="result-value">{result.updated}</span>
              </div>
              <div className="result-item warning">
                <span className="result-label">Productos no encontrados:</span>
                <span className="result-value">{result.notFound}</span>
              </div>
            </div>

            {result.notFoundMessages && result.notFoundMessages.length > 0 && (
              <div className="not-found-messages">
                <h4>Productos No Encontrados:</h4>
                <ul>
                  {result.notFoundMessages.map((msg, idx) => (
                    <li key={idx}>{msg}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.errorMessages && result.errorMessages.length > 0 && (
              <div className="error-messages">
                <h4>Errores Durante la Actualización:</h4>
                <ul>
                  {result.errorMessages.map((msg, idx) => (
                    <li key={idx}>{msg}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="form-actions">
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading || !file || (locationType === 'supermercado' && !selectedSupermercado)}
          >
            {loading ? 'Actualizando...' : 'Actualizar Stock'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default BulkStockUpdate;
